// FT3D move-preview messages consumed by the active scene.

import type { WSMessage } from './types';
import {
    isFt3dMovePreviewClientId,
    isLocalFt3dMovePreviewClient,
} from '../shared/ft3d-move-preview-client';
import { markFt3dEventDelayDiagnostic } from '../shared/ft3d-event-delay-diagnostic';

type MovePreview = Extract<WSMessage, { type: 'ft3d_move_preview' }>;
type MovePreviewClear = Extract<WSMessage, { type: 'ft3d_move_preview_clear' }>;
type MovePreviewMessage = MovePreview | MovePreviewClear;

const latestSequenceByClient = new Map<string, number>();
const recentCommittedMoveIds = new Map<string, number>();
const COMMITTED_MOVE_DEDUPE_MS = 60_000;

function isDuplicateCommittedMove(data: MovePreviewMessage): boolean {
    if (!('committed' in data) || !data.committed) return false;
    const moveId = String(data.move_id ?? '').trim().toLowerCase();
    if (!isFt3dMovePreviewClientId(moveId)) return false;
    const now = Date.now();
    for (const [knownId, expiresAt] of recentCommittedMoveIds) {
        if (expiresAt <= now) recentCommittedMoveIds.delete(knownId);
    }
    if (recentCommittedMoveIds.has(moveId)) return true;
    recentCommittedMoveIds.set(moveId, now + COMMITTED_MOVE_DEDUPE_MS);
    if (recentCommittedMoveIds.size > 256) {
        const oldest = recentCommittedMoveIds.keys().next().value as string | undefined;
        if (oldest) recentCommittedMoveIds.delete(oldest);
    }
    return false;
}

/**
 * The publisher has already painted its immediate local guide/move. Redis echoes are for the
 * other browser clients; replaying one locally creates the delayed second guide seen during drag.
 * Sequence rejection also prevents a slow update request from resurrecting a guide after commit
 * or clear on observers.
 */
function shouldDispatchFt3dMovePreview(data: MovePreviewMessage): boolean {
    const originClientId = String(data.origin_client_instance_id ?? '').trim().toLowerCase();
    if (originClientId && isLocalFt3dMovePreviewClient(originClientId)) return false;
    const clientId = String(data.client_instance_id ?? '').trim().toLowerCase();
    if (clientId && isLocalFt3dMovePreviewClient(clientId)) return false;
    if (isDuplicateCommittedMove(data)) return false;
    if (!isFt3dMovePreviewClientId(clientId)) return true;

    const sequence = Number(data.sequence);
    if (!Number.isSafeInteger(sequence) || sequence <= 0) return true;
    const previous = latestSequenceByClient.get(clientId) ?? 0;
    if (sequence <= previous) return false;
    latestSequenceByClient.set(clientId, sequence);
    if (latestSequenceByClient.size > 256) {
        const oldest = latestSequenceByClient.keys().next().value as string | undefined;
        if (oldest) latestSequenceByClient.delete(oldest);
    }
    return true;
}

function handleFt3dMovePreview(data: MovePreview): void {
    markFt3dEventDelayDiagnostic('parent.websocket.move-handler-entry', data);
    if (!checkIfValidUUID(data.encounter_id)) return;
    if (!shouldDispatchFt3dMovePreview(data)) return;
    window.dispatchEvent(new CustomEvent('ft:ft3d-move-preview', { detail: data }));
    markFt3dEventDelayDiagnostic('parent.websocket.move-handler-exit', data);
}

function handleFt3dMovePreviewClear(data: MovePreviewClear): void {
    if (!checkIfValidUUID(data.encounter_id)) return;
    if (!shouldDispatchFt3dMovePreview(data)) return;
    window.dispatchEvent(new CustomEvent('ft:ft3d-move-preview', { detail: data }));
}

window.handleFt3dMovePreview = handleFt3dMovePreview;
window.handleFt3dMovePreviewClear = handleFt3dMovePreviewClear;

export {
    handleFt3dMovePreview,
    handleFt3dMovePreviewClear,
    shouldDispatchFt3dMovePreview,
};
